import { useState, useMemo, useEffect, type FormEvent } from 'react';
import { useAuth } from '@/lib/auth';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { knoxApi, KnoxApiError } from '@/lib/knox-api';
import { trackSignupConversion } from '@/lib/gtag';
import { loadRecaptchaScript, executeRecaptcha } from '@/lib/recaptcha';
import RecaptchaCheckbox from '@/components/RecaptchaCheckbox';

const RECAPTCHA_SITE_KEY = (window as any).__KNOX_CONFIG__?.RECAPTCHA_SCORE_SITE_KEY || import.meta.env.VITE_RECAPTCHA_SCORE_SITE_KEY || undefined;
const RECAPTCHA_CHECKBOX_SITE_KEY = (window as any).__KNOX_CONFIG__?.RECAPTCHA_CHECKBOX_SITE_KEY || import.meta.env.VITE_RECAPTCHA_CHECKBOX_SITE_KEY || undefined;

type Step = 'email' | 'password' | 'register' | 'set-password' | 'verify-email';

const PASSWORD_RULES = [
  { test: (v: string) => v.length >= 12, labelKey: '12+ characters' },
  { test: (v: string) => /[a-zA-Z]/.test(v), labelKey: 'Contains a letter' },
  { test: (v: string) => /[0-9]/.test(v), labelKey: 'Contains a number' },
  { test: (v: string) => /[^a-zA-Z0-9]/.test(v), labelKey: 'Contains a symbol' },
] as const;

const inputClass =
  'w-full rounded-full border border-black/[0.16] bg-surface-container-lowest py-4 px-6 text-foreground placeholder:text-muted-foreground/40 focus:border-primary/30 focus:outline-none focus:ring-2 focus:ring-primary/10 transition-all duration-300';

const btnPrimaryClass =
  'w-full rounded-full bg-gradient-to-br from-[#0070F3] to-[#005ac6] py-4 font-bold text-white shadow-lg transition-all duration-300 hover:shadow-[0_0_20px_rgba(135,173,255,0.3)] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed';

export function EmailLoginForm({ onStepChange }: { onStepChange?: (step: Step) => void } = {}) {
  const { login, register, setPassword: authSetPassword, error, clearError } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('knox-aiops');

  const [step, setStep] = useState<Step>('email');
  const updateStep = (s: Step) => { setStep(s); onStepChange?.(s); };
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [needChallenge, setNeedChallenge] = useState(false);
  const [challengeToken, setChallengeToken] = useState('');
  const [verifyPurpose, setVerifyPurpose] = useState<'register' | 'set_password'>('register');
  const [emailVerifiedToken, setEmailVerifiedToken] = useState('');
  const [verifyCode, setVerifyCode] = useState(['', '', '', '', '', '']);
  const [verifyError, setVerifyError] = useState('');
  const [resendCountdown, setResendCountdown] = useState(0);
  const [verifying, setVerifying] = useState(false);

  // Load reCAPTCHA score-based script on mount
  useEffect(() => {
    if (RECAPTCHA_SITE_KEY) {
      loadRecaptchaScript(RECAPTCHA_SITE_KEY).catch(() => {
        // Non-fatal: requests will go without token; backend rejects if RECAPTCHA_ENABLED=true
      });
    }
  }, []);

  // Countdown timer for resend verification code
  useEffect(() => {
    if (resendCountdown <= 0) return;
    const timer = setTimeout(() => setResendCountdown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCountdown]);

  const passwordErrors = useMemo(
    () => PASSWORD_RULES.filter((r) => !r.test(password)).map((r) => r.labelKey),
    [password],
  );
  const isPasswordValid = passwordErrors.length === 0;
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  function goBack() {
    clearError();
    updateStep('email');
    setPassword('');
    setConfirmPassword('');
    setName('');
    setNeedChallenge(false);
    setChallengeToken('');
    setVerifyPurpose('register');
    setEmailVerifiedToken('');
    setVerifyCode(['', '', '', '', '', '']);
    setVerifyError('');
    setResendCountdown(0);
    setVerifying(false);
  }

  async function handleEmailSubmit(e: FormEvent) {
    e.preventDefault();
    if (!email) return;
    clearError();
    setNeedChallenge(false);
    setCheckingEmail(true);
    try {
      let recaptchaToken: string | undefined;
      if (RECAPTCHA_SITE_KEY) {
        try { recaptchaToken = await executeRecaptcha('check_email'); } catch { /* not ready */ }
      }
      const result = await knoxApi.checkEmail(email, { recaptchaToken });
      if (!result.exists) {
        try {
          await knoxApi.sendEmailCode(email, 'register');
        } catch { /* silent */ }
        setVerifyPurpose('register');
        setVerifyCode(['', '', '', '', '', '']);
        setVerifyError('');
        setResendCountdown(60);
        updateStep('verify-email');
      } else if (result.has_password) {
        updateStep('password');
      } else {
        try {
          await knoxApi.sendEmailCode(email, 'set_password');
        } catch { /* silent */ }
        setVerifyPurpose('set_password');
        setVerifyCode(['', '', '', '', '', '']);
        setVerifyError('');
        setResendCountdown(60);
        updateStep('verify-email');
      }
    } catch {
      // If check-email fails, fall back to password step
      updateStep('password');
    } finally {
      setCheckingEmail(false);
    }
  }

  async function handlePasswordSubmit(e: FormEvent) {
    e.preventDefault();
    if (!password) return;
    setNeedChallenge(false);
    setSubmitting(true);
    try {
      let recaptchaToken: string | undefined;
      if (RECAPTCHA_SITE_KEY) {
        try { recaptchaToken = await executeRecaptcha('login'); } catch { /* not ready */ }
      }
      await login(email, password, { recaptchaToken });
    } catch (err) {
      if (err instanceof KnoxApiError && err.code === 'NEED_CHALLENGE') {
        setNeedChallenge(true);
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRegisterSubmit(e: FormEvent) {
    e.preventDefault();
    if (!isPasswordValid || !passwordsMatch || !name.trim()) return;
    trackSignupConversion();
    setNeedChallenge(false);
    setSubmitting(true);
    try {
      let recaptchaToken: string | undefined;
      if (RECAPTCHA_SITE_KEY) {
        try { recaptchaToken = await executeRecaptcha('register'); } catch { /* not ready */ }
      }
      await register(email, password, name.trim(), { recaptchaToken, emailVerifiedToken });
      // register handles navigation (org-binding or reload after join)
    } catch (err) {
      if (err instanceof KnoxApiError && err.code === 'NEED_CHALLENGE') {
        setNeedChallenge(true);
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSetPasswordSubmit(e: FormEvent) {
    e.preventDefault();
    if (!isPasswordValid || !passwordsMatch) return;
    setNeedChallenge(false);
    setSubmitting(true);
    try {
      let recaptchaToken: string | undefined;
      if (RECAPTCHA_SITE_KEY) {
        try { recaptchaToken = await executeRecaptcha('set_password'); } catch { /* not ready */ }
      }
      const user = await authSetPassword(email, password, { recaptchaToken, emailVerifiedToken });
      if (user?.default_workspace_id) {
        navigate(`/workspaces/${user.default_workspace_id}`, { replace: true });
      } else {
        navigate('/org-binding', { replace: true });
      }
    } catch (err) {
      if (err instanceof KnoxApiError && err.code === 'NEED_CHALLENGE') {
        setNeedChallenge(true);
      }
    } finally {
      setSubmitting(false);
    }
  }

  // Auto-retry when challenge token is obtained from RecaptchaCheckbox
  useEffect(() => {
    if (!challengeToken) return;
    const retrySubmit = async () => {
      setSubmitting(true);
      try {
        if (step === 'password') {
          await login(email, password, { recaptchaToken: challengeToken, recaptchaType: 'checkbox' });
        } else if (step === 'register') {
          await register(email, password, name.trim(), { recaptchaToken: challengeToken, recaptchaType: 'checkbox', emailVerifiedToken });
        } else if (step === 'set-password') {
          const user = await authSetPassword(email, password, { recaptchaToken: challengeToken, recaptchaType: 'checkbox', emailVerifiedToken });
          if (user?.default_workspace_id) {
            navigate(`/workspaces/${user.default_workspace_id}`, { replace: true });
          } else {
            navigate('/org-binding', { replace: true });
          }
        }
      } catch (err) {
        // Checkbox token also failed — show error via auth context (already set by login/register/setPassword)
        // If it's an unexpected error, surface it
        if (err instanceof KnoxApiError && err.code !== 'NEED_CHALLENGE') {
          // auth context already set the error message via setState
        } else if (!(err instanceof KnoxApiError)) {
          clearError();
        }
      } finally {
        setSubmitting(false);
        setChallengeToken('');
      }
    };
    retrySubmit();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challengeToken]);

  async function handleVerifyCodeSubmit(code: string) {
    setVerifying(true);
    setVerifyError('');
    try {
      const result = await knoxApi.verifyEmailCode(email, code, verifyPurpose);
      setEmailVerifiedToken(result.email_verified_token);
      updateStep(verifyPurpose === 'register' ? 'register' : 'set-password');
    } catch (err) {
      setVerifyError(err instanceof Error ? err.message : 'Invalid verification code');
    } finally {
      setVerifying(false);
    }
  }

  async function handleResendCode() {
    setVerifyError('');
    setVerifyCode(['', '', '', '', '', '']);
    setResendCountdown(60);
    try {
      await knoxApi.sendEmailCode(email, verifyPurpose);
    } catch { /* silent */ }
  }

  // --- Email step ---
  if (step === 'email') {
    return (
      <form onSubmit={handleEmailSubmit} className="flex flex-col gap-6">
        <div>
          <label
            htmlFor="email"
            className="mb-3 block px-1 text-[0.6875rem] font-bold uppercase tracking-widest text-muted-foreground"
          >
            {t('loginForm.email')}
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            required
            autoFocus
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder={t('loginForm.emailPlaceholder')}
            className={inputClass}
          />
        </div>
        <button
          type="submit"
          disabled={!email || checkingEmail}
          className={btnPrimaryClass}
        >
          {checkingEmail ? t('loginForm.checkingEmail') : t('loginForm.continue')}
        </button>
      </form>
    );
  }

  // --- Password login step ---
  if (step === 'password') {
    return (
      <form onSubmit={handlePasswordSubmit} className="flex flex-col gap-6">
        <div>
          <div className="mb-3 flex items-center justify-between px-1">
            <span className="text-sm text-foreground font-medium">{email}</span>
            <button
              type="button"
              onClick={goBack}
              className="text-xs text-primary hover:underline underline-offset-4"
            >
              {t('loginForm.change')}
            </button>
          </div>
          <label
            htmlFor="password"
            className="mb-3 block px-1 text-[0.6875rem] font-bold uppercase tracking-widest text-muted-foreground"
          >
            {t('loginForm.password')}
          </label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            autoFocus
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder={t('loginForm.passwordPlaceholder')}
            className={inputClass}
          />
          <div className="px-1 text-right">
            <Link
              to="/forgot-password"
              state={{ email }}
              className="text-xs text-primary hover:underline underline-offset-4"
            >
              {t('loginForm.forgotPassword')}
            </Link>
          </div>
        </div>

        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}

        {needChallenge && RECAPTCHA_CHECKBOX_SITE_KEY && (
          <RecaptchaCheckbox
            siteKey={RECAPTCHA_CHECKBOX_SITE_KEY}
            action="login"
            onVerified={(token) => {
              setChallengeToken(token);
              setNeedChallenge(false);
            }}
          />
        )}

        <button
          type="submit"
          disabled={submitting || needChallenge || !password}
          className={btnPrimaryClass}
        >
          {submitting ? t('loginForm.signingIn') : t('loginForm.signIn')}
        </button>
      </form>
    );
  }

  // --- Verify-email step (OTP code entry) ---
  if (step === 'verify-email') {
    return (
      <div className="flex flex-col gap-5">
        {/* Email row — same style as password/set-password steps */}
        <div className="mb-1 flex items-center justify-between px-1">
          <span className="text-sm text-foreground font-medium">{email}</span>
          <button type="button" onClick={goBack} className="text-xs text-primary hover:underline underline-offset-4">
            {t('loginForm.change')}
          </button>
        </div>

        {/* OTP inputs */}
        <div className="flex justify-center gap-3">
          {verifyCode.map((digit, i) => (
            <input
              key={i}
              id={`code-${i}`}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              autoFocus={i === 0}
              className="h-14 w-12 rounded-xl border border-black/[0.16] bg-surface-container-lowest text-center text-2xl font-bold text-foreground focus:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all"
              onChange={e => {
                const val = e.target.value.replace(/\D/g, '');
                const next = [...verifyCode];
                next[i] = val.slice(-1);
                setVerifyCode(next);
                if (val && i < 5) {
                  document.getElementById(`code-${i + 1}`)?.focus();
                }
                const full = next.join('');
                if (full.length === 6) handleVerifyCodeSubmit(full);
              }}
              onKeyDown={e => {
                if (e.key === 'Backspace' && !verifyCode[i] && i > 0) {
                  document.getElementById(`code-${i - 1}`)?.focus();
                }
              }}
              onPaste={e => {
                const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
                if (pasted.length === 6) {
                  setVerifyCode(pasted.split(''));
                  handleVerifyCodeSubmit(pasted);
                }
                e.preventDefault();
              }}
            />
          ))}
        </div>

        {/* Error / verifying */}
        {verifyError && (
          <p role="alert" className="text-sm text-destructive text-center">{verifyError}</p>
        )}
        {verifying && (
          <p className="text-sm text-muted-foreground text-center">{t('loginForm.verifying')}</p>
        )}

        {/* Resend */}
        <button
          type="button"
          onClick={handleResendCode}
          disabled={resendCountdown > 0}
          className={btnPrimaryClass}
        >
          {resendCountdown > 0
            ? t('loginForm.resendIn', { seconds: resendCountdown })
            : t('loginForm.resendCode')}
        </button>
      </div>
    );
  }

  // --- Register step (new user) ---
  if (step === 'register') {
    return (
      <form onSubmit={handleRegisterSubmit} className="flex flex-col gap-5">
        <div>
          <div className="mb-3 flex items-center justify-between px-1">
            <span className="text-sm text-foreground font-medium">{email}</span>
            <button
              type="button"
              onClick={goBack}
              className="text-xs text-primary hover:underline underline-offset-4"
            >
              {t('loginForm.change')}
            </button>
          </div>
        </div>

        <div>
          <label
            htmlFor="register-name"
            className="mb-2 block px-1 text-[0.6875rem] font-bold uppercase tracking-widest text-muted-foreground"
          >
            {t('loginForm.name')}
          </label>
          <input
            id="register-name"
            type="text"
            autoComplete="name"
            required
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder={t('loginForm.namePlaceholder')}
            className={inputClass}
          />
        </div>

        <div>
          <label
            htmlFor="register-password"
            className="mb-2 block px-1 text-[0.6875rem] font-bold uppercase tracking-widest text-muted-foreground"
          >
            {t('loginForm.createPassword')}
          </label>
          <input
            id="register-password"
            type="password"
            autoComplete="new-password"
            required
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder={t('loginForm.passwordPlaceholder')}
            className={inputClass}
          />
          <p className="mt-1.5 px-1 text-xs text-muted-foreground/60">
            {t('loginForm.passwordRules')}
          </p>
          {password.length > 0 && passwordErrors.length > 0 && (
            <ul className="text-xs text-destructive space-y-0.5 mt-1 px-1">
              {passwordErrors.map((e) => (
                <li key={e}>- {e}</li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <label
            htmlFor="register-confirm-password"
            className="mb-2 block px-1 text-[0.6875rem] font-bold uppercase tracking-widest text-muted-foreground"
          >
            {t('loginForm.confirmPassword')}
          </label>
          <input
            id="register-confirm-password"
            type="password"
            autoComplete="new-password"
            required
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            placeholder={t('loginForm.confirmPasswordPlaceholder')}
            className={inputClass}
          />
        </div>

        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}

        {needChallenge && RECAPTCHA_CHECKBOX_SITE_KEY && (
          <RecaptchaCheckbox
            siteKey={RECAPTCHA_CHECKBOX_SITE_KEY}
            action="register"
            onVerified={(token) => {
              setChallengeToken(token);
              setNeedChallenge(false);
            }}
          />
        )}

        <button
          type="submit"
          disabled={submitting || needChallenge || !name.trim() || !isPasswordValid || !passwordsMatch}
          className={btnPrimaryClass}
        >
          {submitting ? t('loginForm.signingUp') : t('loginForm.signUp')}
        </button>
      </form>
    );
  }

  // --- Set-password step (OAuth user without password) ---
  return (
    <form onSubmit={handleSetPasswordSubmit} className="flex flex-col gap-5">
      <div>
        <div className="mb-3 flex items-center justify-between px-1">
          <span className="text-sm text-foreground font-medium">{email}</span>
          <button
            type="button"
            onClick={goBack}
            className="text-xs text-primary hover:underline underline-offset-4"
          >
            {t('loginForm.change')}
          </button>
        </div>
      </div>

      <div>
        <label
          htmlFor="set-password"
          className="mb-2 block px-1 text-[0.6875rem] font-bold uppercase tracking-widest text-muted-foreground"
        >
          {t('loginForm.createPassword')}
        </label>
        <input
          id="set-password"
          type="password"
          autoComplete="new-password"
          required
          value={password}
          onChange={e => setPassword(e.target.value)}
          placeholder={t('loginForm.passwordPlaceholder')}
          className={inputClass}
        />
        <p className="mt-1.5 px-1 text-xs text-muted-foreground/60">
          {t('loginForm.passwordRules')}
        </p>
        {password.length > 0 && passwordErrors.length > 0 && (
          <ul className="text-xs text-destructive space-y-0.5 mt-1 px-1">
            {passwordErrors.map((e) => (
              <li key={e}>- {e}</li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <label
          htmlFor="set-confirm-password"
          className="mb-2 block px-1 text-[0.6875rem] font-bold uppercase tracking-widest text-muted-foreground"
        >
          {t('loginForm.confirmPassword')}
        </label>
        <input
          id="set-confirm-password"
          type="password"
          autoComplete="new-password"
          required
          value={confirmPassword}
          onChange={e => setConfirmPassword(e.target.value)}
          placeholder={t('loginForm.confirmPasswordPlaceholder')}
          className={inputClass}
        />
      </div>

      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}

      {needChallenge && RECAPTCHA_CHECKBOX_SITE_KEY && (
        <RecaptchaCheckbox
          siteKey={RECAPTCHA_CHECKBOX_SITE_KEY}
          action="set_password"
          onVerified={(token) => {
            setChallengeToken(token);
            setNeedChallenge(false);
          }}
        />
      )}

      <button
        type="submit"
        disabled={submitting || needChallenge || !isPasswordValid || !passwordsMatch}
        className={btnPrimaryClass}
      >
        {submitting ? t('loginForm.settingPassword') : t('loginForm.setPassword')}
      </button>
    </form>
  );
}
